/**
 * Why a write-off was made, shared by ts-back-end (validation, reporting) and
 * ts-front-end (the posting row's dropdowns).
 *
 * A write-off is coded as a category and a reason within it; only the reason is
 * stored, and the category follows from it, so the two can never disagree. Each
 * reason carries the client's definition (`guidance`) and data-entry rule
 * (`rule`), which the dropdown shows as help text.
 *
 * Write-offs posted from an electronic remittance are not typed by anyone: they
 * carry the payer's own CARC codes on the transaction's `adjustments`, and take
 * their reason from those codes (`deriveWriteOffReason` below), so they read the
 * same as a hand-entered one.
 *
 * NOTE: this file is intentionally plain CommonJS JavaScript (types live in the
 * adjacent .d.ts). The front-end webpack build has no TypeScript loader for
 * files outside its src/, so runtime-imported ts-common modules must be
 * parseable as plain JS.
 */

// A payer adjustment reason, coded by its CAS group and CARC.
const carcReason = (group, code, description) => ({
    value: `${group}_${code}`,
    label: `${group}-${code} (${description})`,
    guidance: `The EOB/ERA adjusts this amount under ${group}-${code}: ${description}`,
});

const CARC_45 = 'Charge exceeds fee schedule/maximum allowable or contracted/legislated fee arrangement';
const CARC_104 = 'Managed care withholding';
const CARC_131 = 'Claim specific negotiated discount';
const CARC_253 = 'Sequestration - reduction in federal payment';
const CARC_P12 = 'Workers\' compensation jurisdictional fee schedule adjustment';

// Order matters: this is the order the dropdowns show them in.
const WRITE_OFF_CATEGORIES = [
    {
        value: 'ADMINISTRATIVE_ADJUSTMENT',
        label: 'Administrative Adjustment',
        reasons: [
            { value: 'SMALL_BALANCE', label: 'Small Balance', guidance: 'The remaining balance was below the approved collection threshold.', rule: 'Apply the established dollar threshold consistently.' },
            { value: 'INVOICE_REVERSAL', label: 'Invoice Reversal / Void Charge', guidance: 'An invoice or charge was reversed because it should no longer remain as an active receivable.', rule: 'Document the reason for the reversal. Do not use for a valid service that resulted in a payer denial.' },
            { value: 'INSURANCE_OVERPAYMENT', label: 'Insurance Overpayment / Credit Balance', guidance: 'Payer activity resulted in a credit balance requiring adjustment, reconciliation, or refund processing.', rule: 'Document the final resolution. Do not use to remove a credit balance when a payer refund or additional research remains required.' },
            { value: 'PATIENT_OVERPAYMENT', label: 'Patient Overpayment / Credit Balance', guidance: 'Patient payment activity resulted in a credit balance requiring adjustment, reconciliation, or refund processing.', rule: 'Resolve the balance under the applicable patient refund requirements and document the final disposition.' },
            { value: 'WRITE_OFF_REVERSAL', label: 'Reversal of Prior Write-Off', guidance: 'A previously posted write-off was reversed because of new information, subsequent payment, recovery activity, or correction of an erroneous adjustment.', rule: 'Reference the original adjustment. Record the reversal amount and reason, and do not report the reversal as a new write-off.' },
        ],
    },
    {
        value: 'ADJUSTMENT',
        label: 'Adjustment',
        reasons: [
            carcReason('CO', '104', CARC_104),
            carcReason('CO', '105', 'Tax withholding'),
            carcReason('CO', '131', CARC_131),
            carcReason('CO', '144', 'Incentive adjustment, e.g. preferred product/service'),
            carcReason('CO', '253', CARC_253),
            carcReason('CO', '45', CARC_45),
            carcReason('CO', 'P12', CARC_P12),
            carcReason('OA', '104', CARC_104),
            carcReason('OA', '131', CARC_131),
            carcReason('OA', '23', 'The impact of prior payer(s) adjudication including payments and/or adjustments'),
            carcReason('OA', '253', CARC_253),
            carcReason('OA', '45', CARC_45),
            carcReason('OA', 'P12', CARC_P12),
            carcReason('PI', '131', CARC_131),
            carcReason('PI', '253', CARC_253),
            carcReason('PI', '45', CARC_45),
            carcReason('PR', '104', CARC_104),
            carcReason('PR', '45', CARC_45),
        ],
    },
    {
        value: 'AUTHORIZATION',
        label: 'Authorization',
        reasons: [
            { value: 'NO_AUTHORIZATION', label: 'No Authorization', guidance: 'Required authorization was not obtained before the service.', rule: 'Use only when authorization was required and no valid authorization existed.' },
            { value: 'INVALID_AUTHORIZATION', label: 'Invalid Authorization', guidance: 'Authorization existed but did not support the billed service, provider, date, or units.', rule: 'Document the specific discrepancy, such as CPT, entity, date, or units.' },
            { value: 'AUTHORIZATION_EXPIRED', label: 'Authorization Expired', guidance: 'Authorization was no longer valid on the date of service.', rule: 'Use only when expiration directly caused the unrecoverable balance.' },
            { value: 'AUTHORIZATION_DENIED', label: 'Authorization Denied', guidance: 'Authorization was requested but the payer declined the request.', rule: 'Do not use if authorization was never requested.' },
            { value: 'REFERRAL_MISSING', label: 'Referral Missing or Invalid', guidance: 'A required referral was absent, expired, or invalid.', rule: 'Confirm the plan required a referral for the billed service.' },
        ],
    },
    {
        value: 'CODING_BILLING',
        label: 'Coding / Billing',
        reasons: [
            { value: 'INVALID_CLAIM_DATA', label: 'Missing or Invalid Claim Data', guidance: 'Required claim data was absent, incomplete, or incorrectly formatted.', rule: 'Use when a more specific coding or enrollment reason does not apply.' },
        ],
    },
    {
        value: 'CONTRACTING_NETWORK',
        label: 'Contracting / Network',
        reasons: [
            { value: 'NON_CONTRACTED', label: 'Non-Contracted', guidance: 'No applicable payer contract existed for the service.', rule: 'Do not use solely because the payer processed the claim out of network.' },
            { value: 'OUT_OF_NETWORK', label: 'Out-of-Network', guidance: 'The service was adjudicated under out-of-network status.', rule: 'Distinguish intentional OON activity from a contracting gap.' },
            { value: 'CONTRACT_DATE_GAP', label: 'Contract Effective-Date Gap', guidance: 'Contract or network dates did not cover the date of service.', rule: 'Record the payer and contract issue in Notes.' },
            { value: 'SERVICE_EXCLUDED_FROM_CONTRACT', label: 'Service Excluded from Contract', guidance: 'The payer contract did not include the billed service or product.', rule: 'Confirm the exclusion against the applicable contract terms.' },
            { value: 'SCA_NOT_OBTAINED', label: 'Single-Case Agreement Not Obtained', guidance: 'A needed single-case agreement was not secured before service.', rule: 'Use only when an SCA was a viable required path.' },
        ],
    },
    {
        value: 'DOCUMENTATION',
        label: 'Documentation',
        reasons: [
            { value: 'MISSING_ORDER', label: 'Missing or Invalid Order', guidance: 'A required provider order or prescription was unavailable or invalid.', rule: 'Use when the order issue caused the unrecoverable balance.' },
            { value: 'MISSING_CLINICAL_RECORDS', label: 'Missing Clinical Records', guidance: 'Required supporting records were unavailable or incomplete.', rule: 'Do not classify as medical necessity unless the payer made that determination.' },
            { value: 'MISSING_PROVIDER_SIGNATURE', label: 'Missing Provider Signature', guidance: 'A required provider signature was missing or invalid.', rule: 'Document the affected order or record in Notes.' },
            { value: 'DOCUMENTATION_NOT_SUPPORTING', label: 'Documentation Does Not Support Service', guidance: 'Available records did not support the billed service, date, or level.', rule: 'Use after confirming the documentation deficiency.' },
        ],
    },
    {
        value: 'ELIGIBILITY_BENEFITS',
        label: 'Eligibility / Benefits',
        reasons: [
            { value: 'INELIGIBLE_ON_DOS', label: 'Ineligible on Date of Service', guidance: 'Coverage was inactive or terminated on the date of service.', rule: 'Verify eligibility for the actual date of service.' },
            { value: 'COORDINATION_OF_BENEFITS', label: 'Coordination of Benefits', guidance: 'Reimbursement could not be obtained due to unresolved COB requirements.', rule: 'Use when COB is the final reason payment cannot be obtained.' },
            { value: 'BENEFIT_EXHAUSTED', label: 'Benefit Exhausted', guidance: 'The applicable benefit or allowed units were exhausted.', rule: 'Confirm benefit exhaustion with payer documentation.' },
            { value: 'NO_OON_BENEFIT', label: 'No Out-of-Network Benefit', guidance: 'The plan provided no out-of-network coverage for the service.', rule: 'Use when the provider was out of network and the plan had no OON benefit.' },
            { value: 'UPDATED_INSURANCE', label: 'Updated Insurance Received', guidance: 'New or corrected insurance information was received after the original balance disposition, creating a valid opportunity to bill or recover payment.', rule: 'Reverse or restore the applicable balance before billing the updated payer. Retain the original adjustment reason for historical reporting.' },
        ],
    },
    {
        value: 'ENROLLMENT_CREDENTIALING',
        label: 'Enrollment / Credentialing',
        reasons: [
            { value: 'PECOS', label: 'PECOS', guidance: 'Payment was lost because of PECOS enrollment, status, or revalidation.', rule: 'Keep separate from general payer enrollment for Medicare visibility.' },
            { value: 'NPI_TIN_ISSUE', label: 'NPI or TIN Issue', guidance: 'Billing NPI, TIN, taxonomy, or enrollment information was incompatible or invalid.', rule: 'Identify the specific identifier issue in Notes.' },
        ],
    },
    {
        value: 'HOME_SLEEP_TEST_DEVICE',
        label: 'Home Sleep Test / Device',
        reasons: [
            { value: 'TEST_NOT_COMPLETED', label: 'Test Not Completed', guidance: 'The patient entered the workflow but did not complete the study.', rule: 'Use when there is no completed billable service.' },
            { value: 'DEVICE_NOT_RETURNED', label: 'Device Not Returned', guidance: 'The device was not returned and financial loss remained.', rule: 'Use for device-loss exposure, not an uncompleted test alone.' },
            { value: 'DEVICE_LOST_OR_DAMAGED', label: 'Device Lost or Damaged in Transit', guidance: 'The device was lost or damaged during outbound or return shipment.', rule: 'Identify shipping direction and carrier status in Notes.' },
            { value: 'REPEAT_TEST_NOT_REIMBURSABLE', label: 'Repeat Test Not Reimbursable', guidance: 'A repeat study was required but was not reimbursable.', rule: 'Capture the underlying repeat-test reason in Notes.' },
            { value: 'TEST_TECHNICALLY_INADEQUATE', label: 'Test Technically Inadequate', guidance: 'The completed test failed technical adequacy requirements.', rule: 'Use when technical inadequacy is documented and no more specific reason applies.' },
        ],
    },
    {
        value: 'INTERNAL_OPERATIONS',
        label: 'Internal Operations',
        reasons: [
            { value: 'REGISTRATION_ERROR', label: 'Registration Error', guidance: 'Incorrect demographic or insurance entry caused the loss.', rule: 'Select only when registration is the confirmed root cause.' },
            { value: 'ELIGIBILITY_VERIFICATION_ERROR', label: 'Eligibility Verification Error', guidance: 'Coverage or benefit information was incorrectly verified or interpreted.', rule: 'Document the missed or incorrect verification detail.' },
            { value: 'FOLLOW_UP_MISSED', label: 'Follow-Up Missed', guidance: 'Required claim or account follow-up was not completed timely.', rule: 'Assign Owner to the party responsible when follow-up was due.' },
        ],
    },
    {
        value: 'MEDICAL_NECESSITY_COVERAGE',
        label: 'Medical Necessity / Coverage',
        reasons: [
            { value: 'NOT_MEDICALLY_NECESSARY', label: 'Not Medically Necessary', guidance: 'Payer determined the service did not meet medical-necessity requirements.', rule: 'Use after the payer medical-necessity determination is confirmed.' },
            { value: 'NON_COVERED', label: 'Non-Covered Service', guidance: 'The patient benefit plan excludes the service.', rule: 'Do not use for eligibility, authorization, or network failures.' },
            { value: 'DIAGNOSIS_COVERAGE_CRITERIA', label: 'Diagnosis or Coverage Criteria', guidance: 'Diagnosis or clinical criteria did not support coverage under payer policy.', rule: 'Capture the payer policy or denial detail in Notes when available.' },
            { value: 'FREQUENCY_LIMIT', label: 'Frequency Limitation Exceeded', guidance: 'The service exceeded a payer frequency or repeat-testing limitation.', rule: 'Use only when frequency limits are the final cause of loss.' },
            { value: 'EXPERIMENTAL', label: 'Experimental or Investigational', guidance: 'The payer classified the service or technology as experimental or investigational.', rule: 'Confirm the classification is stated in the payer determination.' },
            { value: 'FACILITY_STATUS', label: 'Hospice / SNF / Inpatient Status', guidance: 'Reimbursement was unavailable because the patient\'s hospice enrollment or inpatient/SNF status made the service non-payable by the billed payer.', rule: 'Use only when the patient\'s hospice, SNF, or inpatient status is confirmed as the final reason the balance cannot be recovered.' },
        ],
    },
    {
        value: 'OTHER',
        label: 'Other',
        reasons: [
            { value: 'OTHER', label: 'Other / Unknown', guidance: 'The cause cannot be reasonably classified using an established reason.', rule: 'Requires Notes and periodic review for reclassification or a new code.' },
        ],
    },
    {
        value: 'PATIENT_RESPONSIBILITY',
        label: 'Patient Responsibility',
        reasons: [
            { value: 'CHARITY', label: 'Financial Hardship or Charity', guidance: 'Balance was adjusted under an approved financial-assistance process.', rule: 'Must align with the organization hardship or charity policy.' },
            { value: 'BAD_DEBT', label: 'Bad Debt', guidance: 'Valid patient responsibility remained unpaid after collection efforts were exhausted.', rule: 'Do not use as a substitute for small-balance adjustment.' },
            { value: 'PATIENT_DECEASED_OR_BANKRUPTCY', label: 'Patient Deceased or Bankruptcy', guidance: 'Collection was restricted or discharged due to death or bankruptcy.', rule: 'Retain supporting documentation according to policy.' },
            { value: 'PATIENT_DISPUTE', label: 'Patient Dispute or Refusal to Pay', guidance: 'Patient disputed a valid balance or refused payment after resolution efforts.', rule: 'Document outreach and final disposition in Notes.' },
        ],
    },
    {
        value: 'PAYER_CLAIMS_PROCESSING',
        label: 'Payer / Claims Processing',
        reasons: [
            { value: 'APPEAL_EXHAUSTED', label: 'Appeal Exhausted', guidance: 'Available payer review pathways were completed and the adverse decision remained.', rule: 'Use only after applicable recovery options are exhausted.' },
            { value: 'RECOUPMENT_NOT_RECOVERABLE', label: 'Recoupment Not Recoverable', guidance: 'A payer recoupment or takeback could not be overturned or recovered.', rule: 'Document the recoupment reason and recovery actions.' },
            { value: 'PRIMARY_PAID_MAXIMUM', label: 'Primary Paid Maximum', guidance: 'The primary payer paid the applicable maximum amount, and the remaining balance is not appropriately recoverable from another payer or the patient.', rule: 'Confirm the primary payer adjudication. Do not use for a routine contractual adjustment or unresolved secondary claim.' },
        ],
    },
    {
        value: 'TIMELY_FILING',
        label: 'Timely Filing',
        reasons: [
            { value: 'TIMELY_FILING', label: 'Initial Claim Timely Filing', guidance: 'The original claim was not filed within payer requirements.', rule: 'Do not combine with corrected-claim or appeal timely filing.' },
            { value: 'CORRECTED_CLAIM_TIMELY_FILING', label: 'Corrected Claim Timely Filing', guidance: 'A corrected or replacement claim missed the applicable filing deadline.', rule: 'Confirm the original claim was timely before using.' },
            { value: 'APPEAL_TIMELY_FILING', label: 'Appeal Timely Filing', guidance: 'Appeal or reconsideration was not filed by the payer deadline.', rule: 'Use only when the missed appeal deadline caused the final loss.' },
        ],
    },
];

// Every payer-side reason, flat, each knowing its category.
const WRITE_OFF_REASONS = WRITE_OFF_CATEGORIES.flatMap((category) => category.reasons.map((reason) => ({
    ...reason,
    category: category.value,
})));

// A patient write-off forgives what the patient owes, so it has its own short
// list; it reports under Patient Responsibility.
const PATIENT_WRITE_OFF_REASONS = [
    { value: 'PATIENT_DISCOUNT', label: 'Patient discount', category: 'PATIENT_RESPONSIBILITY' },
    { value: 'BAD_DEBT', label: 'Bad debt', category: 'PATIENT_RESPONSIBILITY' },
];

// The routine case: the payer's contract allows less than the charge. Pre-set
// on a pre-filled write-off so a paper EOB stays a confirm-and-save.
const DEFAULT_WRITE_OFF_REASON = 'CO_45';

// The one reason that cannot stand on its own — it says nothing without the note.
const OTHER_WRITE_OFF_REASON = 'OTHER';

// Where reports put a write-off with no reason on record.
const UNSPECIFIED_WRITE_OFF_CATEGORY = 'UNSPECIFIED';

const findReason = (value, reasons) => reasons.find((reason) => reason.value === value);

const isWriteOffReason = (value) => !!findReason(value, WRITE_OFF_REASONS);

const isPatientWriteOffReason = (value) => !!findReason(value, PATIENT_WRITE_OFF_REASONS);

const writeOffReasonLabel = (value, reasons = [...WRITE_OFF_REASONS, ...PATIENT_WRITE_OFF_REASONS]) => {
    const match = findReason(value, reasons);

    return match ? match.label : null;
};

const writeOffCategoryOf = (value) => {
    const match = findReason(value, WRITE_OFF_REASONS) || findReason(value, PATIENT_WRITE_OFF_REASONS);

    return match ? match.category : null;
};

const writeOffCategoryLabel = (value) => {
    const match = WRITE_OFF_CATEGORIES.find((category) => category.value === value);

    return match ? match.label : null;
};

/**
 * A write-off only needs a reason when there is one to explain. Amounts are
 * compared at cent precision so a rounding artefact never demands a reason.
 */
const writeOffNeedsReason = (writeOff) => Number(Number(writeOff || 0).toFixed(2)) > 0;

// CARCs that mean the payer covers none of the charge. ERA posting reduces the
// allowed amount by them when the payer states no AMT*B6 of its own — a
// non-covered charge was never "allowed at" anything.
const NON_COVERED_CARCS = [
    '96', // Non-covered charge(s)
    '119', // Benefit max reached
    '170', // Denied for provider type
    '204', // Not covered service/equipment/drug
    '246', // Non-payable; reporting only
    '252', // Attachment/other documentation required
    '253', // Coverage rules do not permit payment
    '255', // Non-covered – provider contract terms
    '256', // Not payable per contract
    '258', // Service not authorized
    '259', // Procedure not covered in jurisdiction
    '260', // Not payable for provider specialty
    '261', // Not payable due to place of service
    '270', // Invalid diagnosis / not covered based on DX
    '271', // Prior authorization not met / not covered
    '272', // Coverage denied due to frequency limits
    '273', // Benefit reduction – medically unnecessary
    '274', // Clinical criteria not met → not covered
    '275', // Experimental/investigational – not covered
    '276', // Plan exclusion – not covered
    '277', // Non-covered because benefit has ended
];

// The reason each payer code stands for when its group+code is not itself an
// Adjustment reason, checked in this order, so 252 (documentation required) is
// Missing Clinical Records even though it also counts as non-covered. A code
// not listed here means Other: the codes on the row are the explanation.
const CARC_WRITE_OFF_REASONS = [
    { reason: 'TIMELY_FILING', codes: ['29'] },
    { reason: 'NO_AUTHORIZATION', codes: ['197', '62'] },
    { reason: 'INVALID_AUTHORIZATION', codes: ['15'] },
    { reason: 'AUTHORIZATION_EXPIRED', codes: ['198'] },
    { reason: 'AUTHORIZATION_DENIED', codes: ['39'] },
    { reason: 'REFERRAL_MISSING', codes: ['288', '243'] },
    { reason: 'NOT_MEDICALLY_NECESSARY', codes: ['50'] },
    { reason: 'DIAGNOSIS_COVERAGE_CRITERIA', codes: ['11', '167'] },
    { reason: 'FREQUENCY_LIMIT', codes: ['151', '222'] },
    { reason: 'EXPERIMENTAL', codes: ['55', '56'] },
    { reason: 'BENEFIT_EXHAUSTED', codes: ['119'] },
    { reason: 'INELIGIBLE_ON_DOS', codes: ['26', '27', '31', '32', '200'] },
    { reason: 'COORDINATION_OF_BENEFITS', codes: ['19', '22', '23'] },
    { reason: 'UPDATED_INSURANCE', codes: ['109'] },
    { reason: 'OUT_OF_NETWORK', codes: ['242'] },
    { reason: 'CONTRACT_DATE_GAP', codes: ['147'] },
    { reason: 'NPI_TIN_ISSUE', codes: ['185', '208'] },
    { reason: 'MISSING_CLINICAL_RECORDS', codes: ['226', '251', '252'] },
    { reason: 'DOCUMENTATION_NOT_SUPPORTING', codes: ['150'] },
    { reason: 'INVALID_CLAIM_DATA', codes: ['4', '5', '16', '125', '181', '182'] },
    { reason: 'NON_COVERED', codes: NON_COVERED_CARCS },
];

// The groups a payer writes off under — contractual obligations, other
// adjustments and payer-initiated reductions alike. PR is what the patient
// owes, never a write-off. Shared with the ERA posting derivation, so the
// groups that pre-fill the Write-Off column and the groups that explain it
// never drift apart.
const PAYER_SIDE_GROUPS = ['CO', 'OA', 'PI'];

/**
 * The reason a write-off that came in on a remittance stands for, read from the
 * payer's CARC codes on the transaction: the payer-side code carrying the most
 * money decides (CO-45 $162.61 beside CO-253 $2.20 is CO-45). A group+code that
 * is itself an Adjustment reason is that reason; otherwise the code is looked up
 * above. Always a reason, so an ERA write-off never shows without one.
 */
const deriveWriteOffReason = (adjustments) => {
    let decisive = null;

    for (const adjustment of adjustments || []) {
        const group = String((adjustment && adjustment.group) || '').toUpperCase();

        if (!PAYER_SIDE_GROUPS.includes(group)) {
            continue;
        }

        const amount = Number(adjustment.amount);
        const weight = Number.isFinite(amount) ? amount : 0;

        if (!decisive || weight > decisive.weight) {
            decisive = { group, code: String(adjustment.reason || '').trim().toUpperCase(), weight };
        }
    }

    if (!decisive) {
        return OTHER_WRITE_OFF_REASON;
    }

    const asAdjustment = `${decisive.group}_${decisive.code}`;

    if (isWriteOffReason(asAdjustment)) {
        return asAdjustment;
    }

    const match = CARC_WRITE_OFF_REASONS.find((entry) => entry.codes.includes(decisive.code));

    return match ? match.reason : OTHER_WRITE_OFF_REASON;
};

module.exports = {
    WRITE_OFF_CATEGORIES,
    WRITE_OFF_REASONS,
    PATIENT_WRITE_OFF_REASONS,
    DEFAULT_WRITE_OFF_REASON,
    OTHER_WRITE_OFF_REASON,
    UNSPECIFIED_WRITE_OFF_CATEGORY,
    NON_COVERED_CARCS,
    PAYER_SIDE_GROUPS,
    isWriteOffReason,
    isPatientWriteOffReason,
    writeOffReasonLabel,
    writeOffCategoryOf,
    writeOffCategoryLabel,
    writeOffNeedsReason,
    deriveWriteOffReason,
};
